// Fleet reference for list export (FB-11, 2026-11-01, Suggested by Emma). Deliberately generic and
// separable from any one page: a caller supplies its own visible-column order, its own already-
// localized headers (the SAME t() keys the table itself uses, so EN/ES never drifts from the
// screen), and a plain-value getter per column -- this file never knows what a TaxReturn is.
export interface ExcelExportColumn<T> {
  header: string;
  getValue: (row: T) => string | number;
}

/**
 * Builds a single-sheet workbook from already-fetched, already-filtered rows and triggers a
 * browser download. Client-side only -- no network call, no server endpoint.
 *
 * exceljs is dynamically imported (~900KB) so it never loads for a visit that doesn't export.
 */
export async function exportToExcel<T>(filename: string, columns: ExcelExportColumn<T>[], rows: T[]): Promise<void> {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Sheet1');

  sheet.addRow(columns.map((c) => c.header));
  for (const row of rows) {
    sheet.addRow(columns.map((c) => c.getValue(row)));
  }
  sheet.getRow(1).font = { bold: true };
  sheet.columns.forEach((col) => { col.width = 18; });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** `tax-returns-2026-11-01.xlsx`, local-date (not UTC), so it matches what the user sees on screen. */
export function dateStampedFilename(prefix: string, date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${prefix}-${y}-${m}-${d}.xlsx`;
}
